import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseService } from 'src/app/core/base/base.service';
import { Observable } from 'rxjs';
import { ObjectApiInterface } from '../../../../core/base/interfaces/object-api.interface';
import { SupplierContactModel } from '../../../../core/models/supplier-contact.model';
import { AuthService } from '../../../../core/services/auth.service';
import { ActivatedRoute } from '@angular/router';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import {
  SupplierContactReplacementModalComponent,
  ContactReplacementData,
} from './modals/supplier-contact-replacement-modal/supplier-contact-replacement-modal.component';
import { catchError, switchMap, map } from 'rxjs/operators';
import { EMPTY, of, throwError } from 'rxjs';
import { SupplierService } from './supplier.service';
import { ENUM_PERMISSIONS } from '../../../../core/enums/permission.enum';
import { PermissionMessageService } from '../../../../core/services/permission-message.service';

@Injectable({
  providedIn: 'root',
})
export class SupplierContactService extends BaseService<SupplierContactModel> {
  defaultSort = 'label';
  baseUrlApi: string;

  constructor(
    public http: HttpClient,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private supplierService: SupplierService,
    private injector: Injector,
    public authService: AuthService,
    private permissionMessageService: PermissionMessageService,
  ) {
    super(http, 'supplier', authService);
  }

  downloadExcelTemplate(): Observable<ObjectApiInterface<SupplierContactModel>> {
    return this.http.get<ObjectApiInterface<SupplierContactModel>>(
      `${this.baseUrlApi}/download-excel-template-contact`,
    );
  }

  /**
   * Crée un contact avec gestion automatique des conflits d'unicité
   */
  createContactWithConflictHandling(supplierId: string, contactData: any): Observable<any> {
    // Filet unique : la modale contact partagée est ouverte depuis devis/commande/contrat/établissement (PRED-3128).
    if (this.permissionMessageService.blockIfMissingPermission(ENUM_PERMISSIONS.UPDATE_SUPPLIER)) {
      return EMPTY;
    }
    // D'abord vérifier s'il y a des conflits
    return this.checkContactConflicts(supplierId, contactData).pipe(
      switchMap(conflictResult => {
        if (conflictResult.data.data.hasConflict) {
          // Il y a des conflits, afficher la modale
          return this.handleOrderContactConflict(supplierId, contactData, conflictResult.data.data.conflictData);
        } else {
          // Pas de conflit, créer normalement
          return this.supplierService.createContact(supplierId, contactData);
        }
      }),
    );
  }

  /**
   * Vérifie les conflits de contacts pour un supplier
   */
  private checkContactConflicts(supplierId: string, contactData: any): Observable<any> {
    return this.http.post(`${this.baseUrlApi}/${supplierId}/checkContactConflicts`, contactData);
  }

  /**
   * Met à jour un contact avec gestion automatique des conflits d'unicité
   */
  updateContactWithConflictHandling(supplierId: string, contactId: string, contactData: any): Observable<any> {
    if (this.permissionMessageService.blockIfMissingPermission(ENUM_PERMISSIONS.UPDATE_SUPPLIER)) {
      return EMPTY;
    }
    // D'abord vérifier s'il y a des conflits
    return this.checkContactConflictsForUpdate(supplierId, contactId, contactData).pipe(
      switchMap(conflictResult => {
        if (conflictResult.data.data.hasConflict) {
          // Il y a des conflits, afficher la modale
          return this.handleOrderContactConflict(
            supplierId,
            contactData,
            conflictResult.data.data.conflictData,
            contactId,
          );
        } else {
          // Pas de conflit, modifier normalement
          return this.supplierService.updateContact(supplierId, contactData);
        }
      }),
    );
  }

  /**
   * Vérifie les conflits de contacts pour une mise à jour
   */
  private checkContactConflictsForUpdate(supplierId: string, contactId: string, contactData: any): Observable<any> {
    return this.http.put(`${this.baseUrlApi}/${supplierId}/checkContactConflicts/${contactId}`, contactData);
  }

  /**
   * Gère les conflits de contacts "Commande" en affichant la modale de confirmation
   */
  private handleOrderContactConflict(
    supplierId: string,
    contactData: any,
    conflictData: any,
    contactId?: string,
  ): Observable<any> {
    const modalData: ContactReplacementData = {
      conflictingContacts: conflictData.conflictingContacts,
      summary: conflictData.summary,
      newContact: {
        firstName: contactData.firstName,
        lastName: contactData.lastName,
        email: contactData.email,
        phoneNumbers: contactData.phoneNumbers || [],
      },
    };

    const dialogRef = this.dialog.open(SupplierContactReplacementModalComponent, {
      width: '800px',
      maxHeight: '90vh',
      data: modalData,
      disableClose: true,
    });

    return dialogRef.afterClosed().pipe(
      switchMap(result => {
        if (result) {
          // L'utilisateur a confirmé le remplacement
          console.log('DEBUG - Data being sent for replacement:', contactData);
          console.log('DEBUG - contactTypes specifically:', contactData.contactTypes);

          const replacementRequest$ = contactId
            ? this.http.put(`${this.baseUrlApi}/${supplierId}/updateContactWithReplacement/${contactId}`, contactData)
            : this.http.post(`${this.baseUrlApi}/${supplierId}/createContactWithReplacement`, contactData);

          // Après le remplacement, récupérer et émettre les données actualisées
          return replacementRequest$.pipe(
            switchMap(replacementResult => {
              // Récupérer les données actualisées du fournisseur
              return this.http.get(`${this.baseUrlApi}/get/${supplierId}`).pipe(
                map((updatedSupplier: any) => {
                  // Forcer le rafraîchissement complet via le service principal
                  try {
                    const supplierService = this.injector.get(SupplierService);
                    if (supplierService && updatedSupplier?.data) {
                      // Utiliser findById qui déclenche la mise à jour complète
                      supplierService.findById(supplierId).subscribe(
                        refreshedData => {
                          console.log('DEBUG - Supplier data refreshed after replacement:', refreshedData);
                        },
                        error => {
                          console.error('Error refreshing supplier data:', error);
                        },
                      );
                    }
                  } catch (error) {
                    console.warn('Could not inject SupplierService:', error);
                  }

                  return {
                    ...replacementResult,
                    supplierData: updatedSupplier?.data,
                    refreshRequired: true,
                    success: true, // Indiquer explicitement que le remplacement a réussi
                  };
                }),
              );
            }),
          );
        } else {
          // L'utilisateur a annulé
          return throwError({ cancelled: true });
        }
      }),
    );
  }

  /**
   * Crée directement un contact avec remplacement automatique (sans confirmation)
   */
  createContactWithReplacement(supplierId: string, contactData: any): Observable<any> {
    if (this.permissionMessageService.blockIfMissingPermission(ENUM_PERMISSIONS.UPDATE_SUPPLIER)) {
      return EMPTY;
    }
    return this.http.post(`${this.baseUrlApi}/${supplierId}/createContactWithReplacement`, contactData);
  }

  /**
   * Met à jour directement un contact avec remplacement automatique (sans confirmation)
   */
  updateContactWithReplacement(supplierId: string, contactId: string, contactData: any): Observable<any> {
    if (this.permissionMessageService.blockIfMissingPermission(ENUM_PERMISSIONS.UPDATE_SUPPLIER)) {
      return EMPTY;
    }
    return this.http.put(`${this.baseUrlApi}/${supplierId}/updateContactWithReplacement/${contactId}`, contactData);
  }
}
